import { useState } from 'react';
import type {
  CatalogueHazard, CatalogueProtocol, CatalogueTest, CatalogueTestDetail, CursorPage,
  ReferenceRangeRow, TestAnswerOption, TestCategory,
} from '@dru/types';
import { TEST_CATEGORY_LABEL } from '@dru/types';
import { Alert, Button, Checkbox, Dialog, Field, InfoTip, Input, TextField, useIdempotencyKey, Select } from '@dru/ui';
import { Plus, Trash2 } from 'lucide-react';
import { plural } from './RetireAction';
import { api } from '../../../lib/api';
import { fetchAll } from '../../../lib/fetchAll';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage } from '../../../lib/auth';

/**
 * The two catalogue dialogs that are reached from more than one screen: the
 * reference bands of a test, and publishing a protocol version. Both used to
 * live in the masters list; the detail pages open them too, and one copy is
 * one place for the "mandatory tests cannot be dropped" rule to live.
 */

const EMPTY_BAND: ReferenceRangeRow = {
  sex: null, ageMinYears: null, ageMaxYears: null,
  low: null, high: null, criticalLow: null, criticalHigh: null, label: null,
};

/**
 * Reference bands, edited as a set.
 *
 * One band means nothing on its own — a value is "low" only relative to the
 * bands it does not fall into — so they are saved together. Replacing them
 * supersedes the previous set rather than deleting it: a result records the
 * band it was flagged against, and that has to stay answerable.
 */
export function RangesDialog({ test, onClose, onDone }: {
  test: CatalogueTest; onClose: () => void; onDone: () => void;
}) {
  const [rows, setRows] = useState<ReferenceRangeRow[]>(
    test.referenceRanges.length ? test.referenceRanges : [EMPTY_BAND],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set(i: number, patch: Partial<ReferenceRangeRow>) {
    setRows((r) => r.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  }
  const num = (v: string) => (v === '' ? null : Number(v));

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.post(`/tests/${test.id}/reference-ranges`, { ranges: rows });
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  const COLUMNS = [
    ['ageMinYears', 'Age from'], ['ageMaxYears', 'Age to'], ['low', 'Low'], ['high', 'High'],
    ['criticalLow', 'Critical low'], ['criticalHigh', 'Critical high'],
  ] as const;

  return (
    <Dialog
      title={`Reference bands — ${test.name}`}
      info="Leave the critical columns empty where no panic value applies — a critical value is escalated the moment it is entered. A value is flagged against the most specific band that matches the employee. One band per test is clinically wrong: 11.8 g/dL haemoglobin is mild anaemia in an adult male and normal in an adult female."
      onClose={busy ? () => undefined : onClose}
      wide
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy}>Save {plural(rows.length, 'band')}</Button>
        </>
      )}
    >
      <div className="mt-2 space-y-3">
        {/* What saving changes, and for whom, before anything is typed. */}
        <Alert tone="info" title="What saving changes">
          Every result entered from now on is flagged against these bands
          {test.usedByProtocols ? ` — in the ${plural(test.usedByProtocols, 'protocol')} that ${test.usedByProtocols === 1 ? 'asks' : 'ask'} for this test` : ''}.
          {' '}{test.resultCount ? `The ${plural(test.resultCount, 'result')} already recorded keep the band they were flagged against.` : 'No result has been recorded against this test yet.'}
        </Alert>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-small">
          <caption className="sr-only">Reference bands for {test.name}</caption>
          <thead>
            <tr className="border-b border-neutral-200 text-left text-caption font-medium text-neutral-600">
              <th scope="col" className="py-2 pr-2 font-medium">Sex</th>
              {COLUMNS.map(([key, label]) => (
                <th key={key} scope="col" className="py-2 pr-2 font-medium">{label}</th>
              ))}
              <th scope="col" className="py-2"><span className="sr-only">Remove</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-neutral-100">
                <td className="py-1 pr-2">
                  <Select
                    value={r.sex ?? ''}
                    onChange={(e) => set(i, { sex: (e.target.value || null) as never })}
                    className="w-28"
                    aria-label={`Band ${i + 1}: sex`}
                  >
                    <option value="">Any</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </Select>
                </td>
                {COLUMNS.map(([key, label]) => (
                  <td key={key} className="py-1 pr-2">
                    <Input
                      value={r[key] ?? ''}
                      onChange={(e) => set(i, { [key]: num(e.target.value) } as never)}
                      inputMode="decimal"
                      className="w-20 text-right tabular-nums"
                      aria-label={`Band ${i + 1}: ${label.toLowerCase()}`}
                    />
                  </td>
                ))}
                <td className="py-1 text-right">
                  <button
                    type="button"
                    onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                    aria-label={`Remove band ${i + 1}`}
                    title="Remove this band"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-btn text-neutral-500 hover:bg-danger-100 hover:text-danger-600"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        onClick={() => setRows((r) => [...r, EMPTY_BAND])}
        icon={<Plus className="h-3.5 w-3.5" aria-hidden />}
      >
        Add a band
      </Button>

      {rows.length === 0 && (
        <Alert tone="warning" className="mt-3">
          Saving with no band means results for this test are never flagged high, low or critical.
        </Alert>
      )}

      {error && <Alert className="mt-3">{error}</Alert>}
    </Dialog>
  );
}


export function ProtocolDialog({ base, onClose, onDone }: {
  base: CatalogueProtocol | null; onClose: () => void; onDone: () => void;
}) {
  // Complete, because the picker below groups by category and a group showing
  // three of its five tests looks like a group with three tests.
  const tests = useQuery<CatalogueTest[]>(
    () => fetchAll<CatalogueTest>((cursor) => api.get<CursorPage<CatalogueTest>>(
      `/tests?brief=true&limit=200${cursor ? `&cursor=${cursor}` : ''}`,
    )),
    [],
  );
  const hazards = useQuery<CursorPage<CatalogueHazard>>(() => api.get('/hazards'), []);

  const [form, setForm] = useState({
    code: base?.code ?? '',
    name: base?.name ?? '',
    description: base?.description ?? '',
    periodicityMonths: String(base?.periodicityMonths ?? 12),
    certificateValidityMonths: String(base?.certificateValidityMonths ?? 12),
    requiresConsent: base?.requiresConsent ?? true,
  });
  const [hazardIds, setHazardIds] = useState<string[]>(base?.hazards.map((h) => h.id) ?? []);
  const [battery, setBattery] = useState<Record<string, boolean>>(
    Object.fromEntries((base?.tests ?? []).map((t) => [t.id, t.isMandatory])),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idem = useIdempotencyKey();

  const chosen = Object.keys(battery);

  async function submit() {
    setBusy(true); setError(null);
    const body = {
      hazardIds,
      tests: chosen.map((testId) => ({ testId, isMandatory: battery[testId] })),
      name: form.name,
      ...(form.description ? { description: form.description } : {}),
      periodicityMonths: Number(form.periodicityMonths),
      certificateValidityMonths: Number(form.certificateValidityMonths),
      requiresConsent: form.requiresConsent,
    };
    try {
      if (base) {
        await api.post(`/protocols/${base.id}/versions`, body, idem.key());
      } else {
        await api.post('/protocols', { code: form.code.toUpperCase(), ...body }, idem.key());
      }
      idem.rotate();
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  const byCategory = (tests.data ?? []).reduce<Record<string, CatalogueTest[]>>(
    (acc, t) => { (acc[t.category] ??= []).push(t); return acc; }, {},
  );

  const hazardItems = hazards.data?.items ?? [];
  const chosenHazardNames = hazardItems.filter((h) => hazardIds.includes(h.id)).map((h) => h.name);
  const droppedRequired = (base?.tests ?? []).filter((t) => t.isMandatory && !(t.id in battery)).length;
  const periodicityError = Number(form.periodicityMonths) < 1 || Number(form.periodicityMonths) > 120 ? 'Between 1 and 120 months.' : null;
  const validityError = Number(form.certificateValidityMonths) < 1 || Number(form.certificateValidityMonths) > 120 ? 'Between 1 and 120 months.' : null;

  return (
    <Dialog
      title={base ? `Publish version ${base.version + 1} of ${base.code}` : 'Add a protocol'}
      onClose={busy ? () => undefined : onClose}
      wide
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={submit}
            loading={busy}
            disabled={!chosen.length || form.name.trim().length < 2 || (!base && form.code.trim().length < 2) || !!periodicityError || !!validityError}
          >
            {base ? `Publish version ${base.version + 1}` : 'Publish protocol'}
          </Button>
        </>
      )}
    >
      <div className="mt-2 space-y-4">
        {/* Who this lands on, said before anything is typed: a protocol is
            what an exposed worker is examined for. */}
        <Alert tone="info" title="What publishing changes">
          {base
            ? <>Version {base.version} is superseded and kept exactly as it is: every examination raised under it still resolves against its battery. </>
            : null}
          From publication, every new examination for an employee exposed to{' '}
          {chosenHazardNames.length ? <strong>{chosenHazardNames.join(', ')}</strong> : <strong>any hazard — this is a baseline protocol for every employee</strong>}
          {' '}includes these tests.
        </Alert>

        {!base && (
          <TextField label="Code" required value={form.code} mono autoFocus hint="It cannot change later; versions share it."
            onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} />
        )}
        <TextField label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <TextField label="Description (optional)" value={form.description}
          onChange={(v) => setForm({ ...form, description: v })} />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Repeat every (months)" error={periodicityError}
            info="An employee exposed to two hazards gets the shorter cadence and the shorter validity of the two.">
            {(p) => (
              <Input {...p} type="number" min={1} max={120} value={form.periodicityMonths}
                onChange={(e) => setForm({ ...form, periodicityMonths: e.target.value })}
                className="w-28 text-right tabular-nums" />
            )}
          </Field>
          <Field label="Certificate valid for (months)" error={validityError}>
            {(p) => (
              <Input {...p} type="number" min={1} max={120} value={form.certificateValidityMonths}
                onChange={(e) => setForm({ ...form, certificateValidityMonths: e.target.value })}
                className="w-28 text-right tabular-nums" />
            )}
          </Field>
        </div>

        <Checkbox
          checked={form.requiresConsent}
          onChange={(e) => setForm({ ...form, requiresConsent: e.target.checked })}
          label={(
            <span className="inline-flex items-center gap-1">
              Requires consent
              <InfoTip label="About consent">
                Check-in is refused without it, and reception can capture it at the desk.
              </InfoTip>
            </span>
          )}
        />

        <fieldset>
          <legend className="flex items-center gap-1 text-caption text-neutral-700">
            Applies to employees exposed to
            <InfoTip label="About exposures">
              Leave all unticked for a baseline protocol that applies to every employee.
            </InfoTip>
          </legend>
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 rounded-input border border-neutral-200 p-2">
            {hazardItems.map((h) => (
              <label key={h.id} className="flex min-h-[28px] max-md:min-h-10 items-center gap-1.5 text-small">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={hazardIds.includes(h.id)}
                  onChange={() => setHazardIds((s) =>
                    s.includes(h.id) ? s.filter((x) => x !== h.id) : [...s, h.id])}
                />
                <span className="text-neutral-800">{h.name}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="flex w-full flex-wrap items-baseline justify-between gap-2 text-caption text-neutral-700">
            <span className="flex items-center gap-1">
              Tests
              <InfoTip label="About required tests">
                A required test blocks completion until it is captured. An optional one does not.
              </InfoTip>
            </span>
            <span className="text-neutral-500">
              {chosen.length} chosen · {chosen.filter((id) => battery[id]).length} required
            </span>
          </legend>
          {droppedRequired > 0 && (
            <Alert tone="warning" className="mt-2">
              {plural(droppedRequired, 'test')} required by version {base!.version} {droppedRequired === 1 ? 'is' : 'are'} not in this version.
              Employees examined under it will no longer be tested for {droppedRequired === 1 ? 'it' : 'them'}.
            </Alert>
          )}
          <div className="mt-1.5 max-h-64 space-y-3 overflow-y-auto rounded-input border border-neutral-200 p-3">
            {tests.loading && <p className="text-small text-neutral-500">Loading tests…</p>}
            {Object.entries(byCategory).map(([category, list]) => (
              <div key={category}>
                <div className="text-caption font-medium uppercase tracking-wide text-neutral-600">
                  {TEST_CATEGORY_LABEL[category as TestCategory] ?? category}
                </div>
                <div className="mt-1 space-y-0.5">
                  {list.map((t) => {
                    const on = t.id in battery;
                    return (
                      <div key={t.id} className="flex min-h-[28px] max-md:min-h-10 items-center justify-between gap-2">
                        <label className="flex min-w-0 items-center gap-2 text-small">
                          <input
                            type="checkbox"
                            className="h-4 w-4 shrink-0"
                            checked={on}
                            onChange={() => setBattery((b) => {
                              const next = { ...b };
                              if (on) delete next[t.id]; else next[t.id] = true;
                              return next;
                            })}
                          />
                          <span className="truncate text-neutral-800">{t.name}</span>
                          <span className="font-mono text-caption text-neutral-500">{t.code}</span>
                        </label>
                        {on && (
                          <label className="flex shrink-0 items-center gap-1 text-caption text-neutral-700">
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={battery[t.id]}
                              onChange={() => setBattery((b) => ({ ...b, [t.id]: !b[t.id] }))}
                              aria-label={`${t.name} is required`}
                            />
                            Required
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </fieldset>

        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}


/**
 * The allowed answers for an ENUM test, edited as a set.
 *
 * Same shape and same reasoning as the reference-band dialog: an option means
 * nothing on its own, so they are saved together — and an option dropped here
 * is DEACTIVATED rather than deleted, because a result stores the code it was
 * entered with and removing the row would leave that result unexplainable.
 *
 * The abnormal checkbox is the load-bearing one. It is what lets a categorical
 * result be flagged at all: a colour-vision "deficient" is a finding and a
 * "normal" is not, and that is a clinical fact about the option rather than
 * something a bench screen should be deciding by string comparison.
 */
export function AnswerOptionsDialog({ test, onClose, onDone }: {
  test: CatalogueTestDetail; onClose: () => void; onDone: () => void;
}) {
  const [rows, setRows] = useState<TestAnswerOption[]>(
    test.answerOptions.length
      ? test.answerOptions.map((o) => ({ ...o }))
      : [{ code: '', display: '', sequence: 0, isAbnormal: false }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (i: number, patch: Partial<TestAnswerOption>) =>
    setRows(rows.map((r, n) => (n === i ? { ...r, ...patch } : r)));

  const filled = rows.filter((r) => r.code.trim() && r.display.trim());
  const duplicate = (() => {
    const seen = new Set<string>();
    for (const r of filled) {
      const c = r.code.trim().toUpperCase();
      if (seen.has(c)) return c;
      seen.add(c);
    }
    return null;
  })();

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.post(`/tests/${test.id}/answer-options`, {
        options: filled.map((r, i) => ({
          code: r.code.trim().toUpperCase(),
          display: r.display.trim(),
          sequence: i,
          isAbnormal: r.isAbnormal,
        })),
      });
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog
      title={`Allowed answers — ${test.name}`}
      info="The code is what a result stores and what reports count by, so it never changes once results exist — the wording beside it may be changed freely. A technician chooses one of these; there is no free-text box. An answer removed here is retired, not deleted, so past results still explain themselves."
      onClose={busy ? () => undefined : onClose}
      wide
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={!filled.length || !!duplicate}>
            Save {plural(filled.length, 'answer')}
          </Button>
        </>
      )}
    >

      <div className="mt-4 space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Input
              value={r.code}
              onChange={(e) => set(i, { code: e.target.value.toUpperCase() })}
              placeholder="CODE"
              className="w-32 font-mono"
              aria-label={`Answer ${i + 1}: code`}
            />
            <Input
              value={r.display}
              onChange={(e) => set(i, { display: e.target.value })}
              placeholder="What the technician reads"
              className="min-w-0 flex-[1_1_12rem]"
              aria-label={`Answer ${i + 1}: wording`}
            />
            <label className="flex min-h-[32px] max-md:min-h-10 items-center gap-1.5 text-caption text-neutral-700">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={r.isAbnormal}
                onChange={(e) => set(i, { isAbnormal: e.target.checked })}
              />
              Flag as abnormal
            </label>
            <button
              type="button"
              onClick={() => setRows(rows.filter((_, n) => n !== i))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-btn text-neutral-500 hover:bg-danger-100 hover:text-danger-600"
              aria-label={`Remove answer ${i + 1}`}
              title="Remove this answer"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          </div>
        ))}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-2"
        onClick={() => setRows([...rows, { code: '', display: '', sequence: rows.length, isAbnormal: false }])}
        icon={<Plus className="h-3.5 w-3.5" aria-hidden />}
      >
        Add an answer
      </Button>

      {test.retiredAnswerOptions.length > 0 && (
        <p className="mt-3 text-caption text-neutral-500">
          {plural(test.retiredAnswerOptions.length, 'retired answer')} kept so past
          results still explain themselves:{' '}
          {test.retiredAnswerOptions.map((o) => o.code).join(', ')}.
        </p>
      )}

      {duplicate && <Alert className="mt-3">Answer code {duplicate} appears twice. Each must be unique.</Alert>}
      {error && <Alert className="mt-3">{error}</Alert>}
    </Dialog>
  );
}
